import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ButtonDeprecated, YatInput } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { NftMintSteps } from '@app/client';
import { setNewNftName, setNftMintPanelStep } from '@app/store/actions/nft';

import { Typography } from '../../../DesignSystem';
import '../NftSidePanel.scss';

export const RenameNft = () => {
    const { t } = useTranslation(['nft', 'words', 'translation', 'common']);
    const dispatch = useAppDispatch();
    const [nftName, setNftName] = useState<string>('');
    const [longNameAlert, setLongNameAlert] = useState(false);
    const { visualizerName, currentEmoji } = useAppSelector((state) => state.userEmojis);
    const [renameButtonEnabled, setRenameButtonEnabled] = useState<boolean>(false);
    const maxCharCount = 50;

    useEffect(() => {
        if (visualizerName) {
            setNftName(visualizerName);
        }
    }, [visualizerName]);

    useEffect(() => {
        setRenameButtonEnabled(nftName.length <= maxCharCount && nftName !== visualizerName);
        setLongNameAlert(nftName.length >= maxCharCount);
    }, [nftName, longNameAlert, visualizerName]);

    const onNftNameKeyPress = (event) => {
        if (nftName.length >= maxCharCount) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    const renameYat = useCallback(() => {
        if (renameButtonEnabled) {
            dispatch(setNewNftName(nftName));
        }
    }, [dispatch, nftName, renameButtonEnabled]);

    const handleKeyDown = useCallback(
        (e) => {
            // Submit on Enter pressed
            if (e.key === 'Enter') {
                e.preventDefault();
                renameYat();
            }
        },
        [renameYat],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const cancelRename = () => {
        dispatch(setNftMintPanelStep(NftMintSteps.RenameNftCancel));
    };

    return (
        <div className="yat-nft-sidebar__step yat-nft-sidebar__rename-nft">
            <div className="yat-nft-sidebar__flex-item">
                <Typography type="headline" size={40} classes={['yat-nft-sidebar__title']}>
                    {t('rename_your_yat_title')}
                </Typography>
                <Typography type="paragraph" size={16} classes={['yat-nft-sidebar__text']}>
                    <span>{t('rename_your_yat_text')}</span>{' '}
                    <span role="img" aria-label={t('common:emoji', { emoji: 'Wink' })}>
                        😉
                    </span>
                </Typography>

                <div className="yat-nft-sidebar__input-container">
                    <div className="yat-nft-sidebar__input-prefix">
                        <div className="yat-nft-sidebar__input-prefix__item">
                            <Typography type="paragraph" size={20}>
                                <span role="img" aria-label={t('common:emoji', { emoji: t('translation:yat') })}>
                                    {currentEmoji}
                                </span>{' '}
                                |
                            </Typography>
                        </div>
                    </div>
                    <div className="yat-nft-sidebar__input-field">
                        <YatInput
                            type="text"
                            onChange={(e) => setNftName(e.target.value)}
                            onKeyPress={onNftNameKeyPress}
                            classes="yat-nft-sidebar__name-nft-input"
                            placeholder={t('nft:mint_name_your_yat')}
                            value={nftName}
                            autoFocus
                        />
                    </div>
                </div>

                <ButtonDeprecated
                    classes={['nft-sidebar-cta-desktop']}
                    color="primary"
                    disabled={!renameButtonEnabled}
                    onClick={renameYat}
                    size="hero"
                >
                    {t('words:continue')}
                </ButtonDeprecated>
                <ButtonDeprecated
                    classes={['nft-sidebar-cta-mobile']}
                    color="primary"
                    disabled={!renameButtonEnabled}
                    fluid
                    onClick={renameYat}
                    size="large"
                >
                    {t('words:continue')}
                </ButtonDeprecated>

                <div onClick={cancelRename}>
                    <Typography classes={['yat-nft-sidebar__link']} size={14} type="label">
                        {t('skip_for_now')}
                    </Typography>
                </div>

                <div className="nft-sidebar-error-container">
                    {longNameAlert && <p className="yat-nft-sidebar__error-text">{t('nft_name_too_long_message')}</p>}
                </div>
            </div>
        </div>
    );
};
